import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useChatPanelOpen } from '../../hooks/useChatPanelOpen';
import { fetchApi } from '../../api/fetch';

// Open the chat panel and put the cursor in its input. We pull the input by
// `.chat-input` (the convention used by ChatPanel, ChatPopOut, MobileChatView)
// so this works whichever surface ended up mounted. If `preload` is provided
// we dispatch the chat-input:set CustomEvent that ChatPanel listens for so
// the textarea is pre-filled with a starter prompt (ChatPanel handles focus
// + caret in that path; we still poll-focus as a fallback when there's no
// preload).
function focusChatInput(preload?: string): void {
  if (preload !== undefined) {
    const tryDispatch = (attempt = 0) => {
      if (document.querySelector('.chat-input')) {
        window.dispatchEvent(new CustomEvent('danny:chat-input:set', { detail: preload }));
        return;
      }
      if (attempt < 20) setTimeout(() => tryDispatch(attempt + 1), 50);
    };
    tryDispatch();
    return;
  }
  const tryFocus = (attempt = 0) => {
    const el = document.querySelector<HTMLElement>('.chat-input');
    if (el) {
      el.focus();
      el.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (attempt < 10) setTimeout(() => tryFocus(attempt + 1), 50);
  };
  tryFocus();
}

interface Step {
  key: keyof Pick<ReturnType<typeof useOnboarding>,
    'emailDone' | 'digestDone' | 'chatTaskDone' | 'breakdownDone' | 'calendarDone'>;
  label: string;
  cta: string;
  hint: string;
  onCta: () => void;
}

interface StepListProps {
  steps: Step[];
  expanded: string | null;
  setExpanded: (k: string | null) => void;
  onboarding: ReturnType<typeof useOnboarding>;
  toggleStep: (step: Step, done: boolean) => Promise<void>;
  onAfterCta?: () => void;
}

// Shared step rows — used by both the desktop sidebar AND the mobile bottom sheet.
function StepList({ steps, expanded, setExpanded, onboarding, toggleStep, onAfterCta }: StepListProps) {
  return (
    <>
      {steps.map((step) => {
        const done = onboarding[step.key];
        const isExpanded = expanded === step.key && !done;

        return (
          <div
            key={step.key}
            className={[
              'border-l-2 transition-all cursor-pointer',
              done
                ? 'border-transparent opacity-50'
                : isExpanded
                ? 'border-brand-2 bg-brand-2/5 dark:bg-brand-2/10'
                : 'border-transparent hover:bg-zinc-100/50 dark:hover:bg-zinc-900/30',
            ].join(' ')}
            onClick={() => !done && setExpanded(step.key)}
          >
            <div className="flex items-center gap-2.5 px-4 py-3">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void toggleStep(step, done);
                }}
                title={done ? 'Mark as not done' : 'Mark as done'}
                aria-label={done ? 'Mark as not done' : 'Mark as done'}
                className={[
                  'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors',
                  done
                    ? 'border-brand-2 bg-brand-2 hover:opacity-80'
                    : 'border-zinc-300 dark:border-zinc-600 hover:border-brand-2 dark:hover:border-brand-2',
                ].join(' ')}
              >
                {done && <span className="text-white text-xs leading-none">✓</span>}
              </button>
              <span
                className={`text-sm font-medium ${
                  done ? 'line-through text-zinc-500 dark:text-zinc-600' : 'text-zinc-700 dark:text-zinc-300'
                }`}
              >
                {step.label}
              </span>
            </div>

            {isExpanded && !done && (
              <div className="px-4 pb-3 pl-10">
                {step.hint && (
                  <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-2 leading-relaxed">{step.hint}</p>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    step.onCta();
                    onAfterCta?.();
                  }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-brand-2 text-white hover:bg-brand-2/90 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-surface-raised-dark transition-colors"
                >
                  {step.cta}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// Dev-only reset button — shared between desktop sidebar header and the mobile sheet header.
function DevResetButton({ onReset }: { onReset: () => void }) {
  if (!import.meta.env.DEV) return null;
  return (
    <button
      onClick={async () => {
        if (!confirm('Reset onboarding state? Wizard tasks will be deleted.')) return;
        try {
          await fetchApi('/onboarding/reset', { method: 'POST', body: JSON.stringify({}) });
          onReset();
        } catch (err) {
          console.warn('[Checklist] reset failed', err);
        }
      }}
      title="Dev only: reset onboarding state"
      className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-primary-500 transition-colors"
    >
      reset
    </button>
  );
}

function ProgressBar({ pct, doneCount, total }: { pct: number; doneCount: number; total: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-zinc-200 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-brand-2 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-zinc-600 dark:text-zinc-400 whitespace-nowrap">{doneCount} of {total}</span>
    </div>
  );
}

interface GettingStartedChecklistProps {
  // True when the mobile chat tab is active (MobileChatView mounts a fixed
  // bottom composer). Drives the floating pill's bottom offset so it clears
  // the composer on chat but sits low in the chrome band on the tasks tab.
  isChatActive?: boolean;
}

// Floating "Finish setup" pill offsets, exported for testing (jsdom drops
// max()/env() from element.style, so tests assert these strings directly).
// Chat tab: 7.5rem clears MobileChatView's fixed composer.
// Tasks tab: reclaim ~1rem from the home-indicator inset (the device
// over-reserves it) so the pill/FAB sit near the bottom, aligned with
// MobileBottomFade, instead of half a button-height above it.
export const PILL_BOTTOM_CHAT = 'calc(7.5rem + env(safe-area-inset-bottom, 0px))';
export const PILL_BOTTOM_TASKS = 'max(0.25rem, calc(env(safe-area-inset-bottom, 0px) - 1rem))';

export function GettingStartedChecklist({ isChatActive = false }: GettingStartedChecklistProps = {}) {
  const onboarding = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpen: setChatPanelOpen } = useChatPanelOpen();

  // Open the chat panel + focus its input; if we're not already on /app,
  // navigate there first so the panel has somewhere to render. Optionally
  // pre-fill the input with a starter prompt so the user can hit Enter
  // instead of typing the suggestion themselves.
  const openChatPanel = (preload?: string) => {
    if (!location.pathname.startsWith('/app')) navigate('/app');
    // Desktop: open the side drawer. `setChatPanelOpen` only controls the
    // desktop drawer though — on mobile the chat surface (MobileChatView)
    // mounts off the dashboard's `mobileTab`, which this doesn't touch. So
    // also broadcast a dedicated open-intent the dashboard listens for to
    // switch the mobile tab to chat. Harmless on desktop (no listener there).
    setChatPanelOpen(true);
    window.dispatchEvent(new CustomEvent('danny:chat:open'));
    focusChatInput(preload);
  };
  // Default-expand the first incomplete step that has actionable guidance
  // (the wizard steps — email + digest — are already done by the time the
  // checklist appears, so default to chatTaskDone).
  const [expanded, setExpanded] = useState<string | null>('chatTaskDone');
  // Mobile bottom-sheet open state. Desktop sidebar is always visible.
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  if (onboarding.onboardingComplete) return null;

  const steps: Step[] = [
    {
      key: 'emailDone',
      // Path-agnostic label: works whether the user replied to the welcome
      // email or used the in-app fallback textarea. Either way, "tasks into
      // Danny" is the milestone.
      label: 'Get your first tasks into Danny',
      cta: '✓ Done',
      hint: '',
      onCta: () => {},
    },
    {
      key: 'digestDone',
      label: 'Pick a morning briefing time',
      cta: 'Open settings →',
      hint: 'Choose when Danny emails your daily plan (or keep it off).',
      onCta: () => navigate('/settings'),
    },
    {
      key: 'chatTaskDone',
      label: 'Add a task via chat',
      cta: 'Open chat →',
      hint: 'Try: "Add buy milk to my list"',
      onCta: () => openChatPanel(),
    },
    {
      key: 'breakdownDone',
      label: 'Break down a task',
      cta: 'Try it →',
      hint: 'Try: "Break down my last task"',
      onCta: () => openChatPanel('Break down my last task'),
    },
    {
      key: 'calendarDone',
      label: 'Connect Google Calendar',
      cta: 'Connect →',
      hint: 'Sync your calendar so Danny can plan around it.',
      onCta: () => navigate('/settings/integrations'),
    },
  ];

  const doneCount = steps.filter((s) => onboarding[s.key]).length;
  const remaining = steps.length - doneCount;
  const pct = (doneCount / steps.length) * 100;

  const pillBottom = isChatActive ? PILL_BOTTOM_CHAT : PILL_BOTTOM_TASKS;

  const toggleStep = async (step: Step, done: boolean) => {
    try {
      await fetchApi('/onboarding/step', {
        method: 'POST',
        body: JSON.stringify({ step: step.key, done: !done }),
      });
      onboarding.refetch();
    } catch (err) {
      console.warn('[Checklist] toggle failed', err);
    }
  };

  return (
    <>
      {/* Desktop sidebar — visible at md+ */}
      <aside className="hidden md:flex w-64 flex-shrink-0 flex-col border-l border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
        <div className="px-4 py-4 border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-zinc-900 dark:text-zinc-200">Getting started</p>
            <DevResetButton onReset={onboarding.refetch} />
          </div>
          <ProgressBar pct={pct} doneCount={doneCount} total={steps.length} />
        </div>

        <div className="flex-1 overflow-y-auto">
          <StepList
            steps={steps}
            expanded={expanded}
            setExpanded={setExpanded}
            onboarding={onboarding}
            toggleStep={toggleStep}
          />
        </div>
      </aside>

      {/* Mobile floating pill — visible only on <md, only when work remains.
          Its bottom offset is tab-aware (see pillBottom): on the chat tab it
          sits high to clear MobileChatView's fixed bottom composer; on the
          tasks tab there is no bottom composer or tab bar, so it drops into the
          low chrome band and never paints over a task row mid-list.
          Tap to open the full-screen sheet with the same steps + handlers. */}
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setMobileSheetOpen(true)}
          style={{ bottom: pillBottom }}
          className="md:hidden fixed left-1/2 -translate-x-1/2 z-30 px-4 py-2 rounded-full bg-brand-2 text-white text-xs font-semibold shadow-lg hover:bg-brand-2/90 transition-colors"
          aria-label={`Finish setup — ${remaining} step${remaining === 1 ? '' : 's'} remaining`}
        >
          Finish setup • {remaining} left
        </button>
      )}

      {/* Mobile bottom sheet — backdrop + slide-up panel containing the same StepList */}
      {mobileSheetOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 flex flex-col justify-end"
          role="dialog"
          aria-modal="true"
          aria-label="Getting started"
          onClick={() => setMobileSheetOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative bg-white dark:bg-zinc-950 rounded-t-2xl max-h-[80vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 pt-3 pb-2 border-b border-zinc-200 dark:border-zinc-800">
              <div className="flex justify-center mb-2">
                <div className="w-10 h-1 rounded-full bg-zinc-300 dark:bg-zinc-700" />
              </div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-zinc-900 dark:text-zinc-200">Getting started</p>
                <div className="flex items-center gap-3">
                  <DevResetButton onReset={onboarding.refetch} />
                  <button
                    onClick={() => setMobileSheetOpen(false)}
                    className="text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200"
                    aria-label="Close"
                  >
                    Close
                  </button>
                </div>
              </div>
              <ProgressBar pct={pct} doneCount={doneCount} total={steps.length} />
            </div>
            <div className="flex-1 overflow-y-auto safe-area-bottom">
              <StepList
                steps={steps}
                expanded={expanded}
                setExpanded={setExpanded}
                onboarding={onboarding}
                toggleStep={toggleStep}
                onAfterCta={() => setMobileSheetOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
